import { FONT_FAMILIES } from '@/constant';
import { PropsWithPreview, SlideComponentText } from '@/types/slide';
import { Box, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Props {
    autoFocus: boolean;
    model: SlideComponentText;
    onUpdate?: (v: string) => void;
}

export const TextContent: React.FC<PropsWithPreview<Props>> = ({ preview, autoFocus, model, onUpdate }) => {
    const { t } = useTranslation();

    return (
        <Box sx={{ p: 1, width: '100%' }}>
            {preview ? (
                <Typography
                    sx={{
                        WebkitUserSelect: 'none',
                        MozUserSelect: 'none',
                        msUserSelect: 'none',
                        userSelect: 'none',
                        px: 0.5,
                        py: 1,
                        fontFamily: model.content?.typeSettings?.fontFamily ?? FONT_FAMILIES[0]?.fontFamily,
                        fontSize: `${model.content.fontSize}px`,
                        lineHeight: '120%',
                        wordWrap: 'break-word',
                        color: (theme) => (model.content.text ? model.content.color : (theme.vars || theme).palette.grey[400]),
                        ...(model.content?.typeSettings?.formatting &&
                            model.content?.typeSettings?.formatting?.includes('bold') && {
                                fontWeight: 'bold',
                            }),
                        ...(model.content?.typeSettings?.formatting &&
                            model.content?.typeSettings?.formatting?.includes('italic') && {
                                fontStyle: 'italic',
                            }),
                        ...(model.content?.typeSettings?.formatting &&
                            model.content?.typeSettings?.formatting?.includes('underlined') && {
                                textDecoration: 'underline',
                            }),
                        ...(model.content?.typeSettings?.alignment &&
                            model.content?.typeSettings?.alignment?.includes('left') && {
                                textAlign: 'left',
                            }),
                        ...(model.content?.typeSettings?.alignment &&
                            model.content?.typeSettings?.alignment?.includes('center') && {
                                textAlign: 'center',
                            }),
                        ...(model.content?.typeSettings?.alignment &&
                            model.content?.typeSettings?.alignment?.includes('right') && {
                                textAlign: 'right',
                            }),
                    }}
                >
                    {model.content.text || t('slide.components.assets.text.placeholder')}
                </Typography>
            ) : (
                <TextField
                    disabled={preview}
                    variant='outlined'
                    autoFocus={autoFocus}
                    sx={{
                        '& .MuiInputBase-root': {
                            px: 0.5,
                            py: 1,
                            height: 'auto',
                            minHeight: `${model.content.fontSize}px`,
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: model.content.color,
                            pointerEvents: 'auto',
                        },
                        '& .MuiOutlinedInput-input': {
                            fontFamily: model.content?.typeSettings?.fontFamily ?? FONT_FAMILIES[0]?.fontFamily,
                            fontSize: `${model.content.fontSize}px`,
                            lineHeight: '120%',
                            ...(model.content?.typeSettings?.formatting &&
                                model.content?.typeSettings?.formatting?.includes('bold') && {
                                    fontWeight: 'bold',
                                }),
                            ...(model.content?.typeSettings?.formatting &&
                                model.content?.typeSettings?.formatting?.includes('italic') && {
                                    fontStyle: 'italic',
                                }),
                            ...(model.content?.typeSettings?.formatting &&
                                model.content?.typeSettings?.formatting?.includes('underlined') && {
                                    textDecoration: 'underline',
                                }),
                            ...(model.content?.typeSettings?.alignment &&
                                model.content?.typeSettings?.alignment?.includes('left') && {
                                    textAlign: 'left',
                                }),
                            ...(model.content?.typeSettings?.alignment &&
                                model.content?.typeSettings?.alignment?.includes('center') && {
                                    textAlign: 'center',
                                }),
                            ...(model.content?.typeSettings?.alignment &&
                                model.content?.typeSettings?.alignment?.includes('right') && {
                                    textAlign: 'right',
                                }),
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                            backgroundColor: 'transparent',
                        },
                    }}
                    placeholder={t('slide.components.assets.text.placeholder')}
                    defaultValue={model.content.text}
                    onChange={(e) => onUpdate?.(e.target.value)}
                    fullWidth
                    multiline
                />
            )}
        </Box>
    );
};
